export function getSubscriptions(email) {
  const url = `/app-services/sendgrid?user_email=${email}`;
  return fetch(url, {
    credentials: 'include',
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
    .then(response => response.json());
}

export function postSubscriptions(userEmail, subscriptionsToSuppress) {
  const obj = { user_email: userEmail, subscriptions_to_suppress: subscriptionsToSuppress };
  const url = '/app-services/sendgrid';
  return fetch(url, {
    credentials: 'include',
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(obj),
  })
    .then(response => response.json());
}

export function deleteSubscriptions(userEmail, suppressionsToDelete) {
  const obj = { user_email: userEmail, suppressions_to_delete: suppressionsToDelete };
  const url = '/app-services/sendgrid';
  return fetch(url, {
    credentials: 'include',
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(obj),
  })
    .then(response => response.json());
}

/**
 * Creates a new notification preference for a pixel anomaly.
 *
 * @function postPixelAnomalyNotificationPreference
 * @param {Object} obj - The payload for creating a new notification preference.
 * @param {string} obj.action - The conversion (eg 'conv.purchase') associated with the pixel anomaly.
 * @param {string} obj.anomaly_id - Unique identifier for the anomaly.
 * @param {string} obj.anomaly_type - Type of the anomaly (e.g., 'volume_decrease', 'missing_param').
 * @param {string} obj.status - The new status of the notification preference (e.g., 'active', 'snoozed').
 * @param {string} obj.param - The specific parameter impacted by the anomaly.
 * @returns {Promise<Object>} Resolves to the JSON response of the API call.
 * @description This function sends a POST request to create a new notification preference
 *              for a client-specific pixel anomaly, where details such as the anomaly type
 *              and action are specified in the payload.
 */
export function postPixelAnomalyNotificationPreference(obj) {
  const url = '/app-services/pixel-anomaly-client-notifications/preference';
  return fetch(url, {
    credentials: 'include',
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(obj),
  }).then(response => response.json());
}

/**
 * Updates the status of an existing notification preference for a pixel anomaly.
 *
 * @function putPixelAnomalyNotificationPreference
 * @param {Object} obj - The payload for updating an existing notification preference.
 * @param {string} obj.anomaly_id - Unique identifier for the anomaly.
 * @param {string} obj.status - The new status of the notification preference (e.g., 'active', 'inactive', 'snoozed').
 * @returns {Promise<Object>} Resolves to the JSON response of the API call.
 * @description This function sends a PUT request to update the notification preference
 *              status for an existing pixel anomaly based on the provided anomaly ID.
 */
export function putPixelAnomalyNotificationPreference(obj) {
  const url = '/app-services/pixel-anomaly-client-notifications/preference';
  return fetch(url, {
    credentials: 'include',
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(obj),
  }).then(response => response.json());
}

/**
 * Retrieves all scheduled report configurations for an advertiser.
 *
 * @function getScheduledReportConfigurations
 * @returns {Promise<Object>} Resolves to the JSON response of the API call.
 * @description This function sends a GET request to retrieve all scheduled report configurations.
 */
export function getScheduledReportConfigurations() {
  const url = '/app-services/scheduled-report-configuration';
  return fetch(url, {
    credentials: 'include',
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  }).then(response => response.json());
}

/**
 * Creates a new scheduled report configuration.
 *
 * @function postScheduledReportConfiguration
 * @param {Object} obj - The payload for creating a new scheduled report configuration.
 * @returns {Promise<Object>} Resolves to the JSON response of the API call.
 * @description This function sends a POST request to create a new scheduled report configuration.
 */
export function postScheduledReportConfiguration(obj) {
  const url = '/app-services/scheduled-report-configuration';
  return fetch(url, {
    credentials: 'include',
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(obj),
  }).then(response => response.json());
}

/**
 * Updates a scheduled report configuration.
 *
 * @function putScheduledReportConfiguration
 * @param {Object} obj - The payload for updating a scheduled report configuration.
 * @returns {Promise<Object>} Resolves to the JSON response of the API call.
 * @description This function sends a PUT request to update a scheduled report configuration.
 */
export function putScheduledReportConfiguration(obj) {
  const url = '/app-services/scheduled-report-configuration';
  return fetch(url, {
    credentials: 'include',
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(obj),
  }).then(response => response.json());
}

/**
 * Deletes a scheduled report configuration.
 *
 * @function deleteScheduledReportConfiguration
 * @param {int} id - The ID of the scheduled report configuration to delete.
 * @returns {Promise<Object>} Resolves to the JSON response of the API call.
 * @description This function sends a DELETE request to delete a scheduled report configuration.
 */
export function deleteScheduledReportConfiguration(id) {
  const url = `/app-services/scheduled-report-configuration?id=${id}`;
  return fetch(url, {
    credentials: 'include',
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  }).then(response => response.json());
}

/**
 * Retrieves all report deliveries for an advertiser.
 *
 * @function getReportDeliveries
 * @returns {Promise<Object>} Resolves to the JSON response of the API call.
 * @description This function sends a GET request to retrieve all report deliveries.
 */
export function getReportDeliveries() {
  const url = '/app-services/report-delivery-accounting';
  return fetch(url, {
    credentials: 'include',
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  }).then(response => response.json());
}

export function getMMMFeatureAliases() {
  const url = '/app-services/mmm-feature-aliases';
  return fetch(url, {
    credentials: 'include',
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
    .then(response => response.json());
}
